import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AuthenticationFactor, SecurityOverview, securityApi, securityErrorCode } from "../../backend/security";
import { getUser } from "../../utils/local-storage";
import { signInAgain } from "../../utils/mfa-step-up";
import { forgetPasskey } from "../../utils/passkey-hint";
import { useReauthGate } from "./useReauthGate";
import { EnrollmentMethod } from "./useSecurityEnrollment";

const AUTO_SIGN_IN_SECONDS = 5;

export type Notify = (kind: "success" | "error", message: string, description?: string) => void;

export function factorName(factor: AuthenticationFactor) {
    return factor.label || (factor.type === "totp" ? "Authenticator app" : "Passkey");
}

/** The account security page, shared by the classic and the new UI's views */
export function useAccountSecurity(notify: Notify) {
    const location = useLocation();
    const state = location.state as { mfaRequired?: boolean; intendedPath?: string; projectName?: string } | null;
    const mfaRequired = state?.mfaRequired;
    const projectName = state?.projectName;
    const intendedPath = state?.intendedPath || sessionStorage.getItem("postMfaRedirect") || "/projects";

    const [overview, setOverview] = useState<SecurityOverview>();
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [enrollmentMethod, setEnrollmentMethod] = useState<EnrollmentMethod>();
    const [enrolling, setEnrolling] = useState(false);
    const [enrolled, setEnrolled] = useState(false);
    const [countdown, setCountdown] = useState<number>();
    const reauth = useReauthGate();

    /**
     * `background` refreshes the data without swapping the page for a skeleton.
     * The loading branch returns before the enrollment dialog is rendered, so a
     * foreground refresh unmounts the dialog and it reopens at its first step.
     */
    const loadOverview = useCallback(async ({ background = false } = {}) => {
        try {
            if (!background) {
                setLoading(true);
                setLoadFailed(false);
            }
            setOverview(await securityApi.getOverview());
        } catch {
            // A background refresh keeps the settings already on screen; only a
            // first load has nothing to fall back to and shows the retry state.
            if (!background) {
                setLoadFailed(true);
            }
        } finally {
            if (!background) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    // After enrolling under a project MFA policy the session still has no
    // two-step verification, so send the user back through sign-in.
    useEffect(() => {
        if (countdown === undefined) {
            return;
        }
        if (countdown <= 0) {
            signInAgain(intendedPath);
            return;
        }
        const timer = setTimeout(() => setCountdown(current => (current === undefined ? undefined : current - 1)), 1000);
        return () => clearTimeout(timer);
    }, [countdown, intendedPath]);

    const setPromptPreference = async (enabled: boolean) => {
        try {
            const preferences = await securityApi.updatePreferences(!enabled);
            setOverview(current => current && { ...current, preferences });
        } catch (error: any) {
            notify("error", "Couldn't save preference", error?.response?.data?.message || "Please try again.");
        }
    };

    const removeFactor = async (factor: AuthenticationFactor) => {
        try {
            await securityApi.removeFactor(factor.id);
            // With no passkey left, sign-in here shouldn't open a prompt for one
            if (factor.type === "passkey" && !overview?.authenticationFactors.some(other => other.type === "passkey" && other.id !== factor.id)) {
                forgetPasskey(getUser()?.email);
            }
            notify("success", `${factor.type === "totp" ? "Authenticator app" : "Passkey"} removed`);
            await loadOverview({ background: true });
        } catch (error: any) {
            if (securityErrorCode(error) === "REAUTHENTICATION_REQUIRED") {
                reauth.require(() => removeFactor(factor));
                return;
            }
            notify("error", "Couldn't remove sign-in method", error?.response?.data?.message || "Please try again.");
        }
    };

    const renameFactor = async (factor: AuthenticationFactor, name: string) => {
        const label = name.trim();
        if (!label || label === factor.label) {
            return;
        }
        try {
            await securityApi.renameFactor(factor.id, label);
            await loadOverview({ background: true });
        } catch (error: any) {
            notify("error", "Couldn't rename this passkey", error?.response?.data?.message || "Please try again.");
        }
    };

    const startEnrollment = (method: EnrollmentMethod) => {
        setEnrollmentMethod(method);
        setEnrolling(true);
    };

    const closeEnrollment = () => {
        setEnrolling(false);
        // Only start the auto sign-in once the user has left the dialog.
        if (mfaRequired && enrolled) {
            setCountdown(AUTO_SIGN_IN_SECONDS);
        }
    };

    const completeEnrollment = () => {
        setEnrolled(true);
        // Background, so the dialog survives to show its final step.
        loadOverview({ background: true });
    };

    const factors = overview?.authenticationFactors || [];
    const providers = overview?.federatedProviders.length ? overview.federatedProviders.join(" and ") : "your identity provider";

    return {
        mfaRequired, projectName, intendedPath,
        overview, loading, loadFailed, loadOverview,
        factors,
        hasTotp: factors.some(factor => factor.type === "totp"),
        federated: overview?.federated === true,
        providers,
        enrollmentMethod, enrolling, enrolled, startEnrollment, closeEnrollment, completeEnrollment,
        countdown, cancelCountdown: () => setCountdown(undefined),
        setPromptPreference, removeFactor, renameFactor,
        reauth,
    };
}
